import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock, FileCheck2 } from "lucide-react";

const FEATURES = [
  { Icon: Lock, label: "AES-256 encrypted vault" },
  { Icon: ShieldCheck, label: "SHA-256 chain of custody" },
  { Icon: FileCheck2, label: "Court-ready Bates PDFs" },
];

export default function Splash({ onEnter }) {
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" data-testid="splash-view" style={{ background: "var(--ek-page)" }}>
      {/* ambient layers */}
      <div className="absolute inset-0 ek-scanline opacity-60 pointer-events-none" />
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(46,130,246,0.16), transparent 70%)" }}
      />

      <header className="relative z-10 max-w-6xl mx-auto w-full px-6 h-16 flex items-center gap-3">
        <img src="/ek-logo.png" alt="EvidenceKeeper" className="h-9 w-9 object-contain" />
        <span className="font-head font-bold tracking-tight">EvidenceKeeper</span>
      </header>

      <main className="relative z-10 flex-1 flex items-center">
        <div className="max-w-3xl mx-auto w-full px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="ek-stamp inline-block mb-6" data-testid="splash-tagline">DOCUMENT · PRESERVE · PROTECT</div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="font-head font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            A calm, private record of
            <span className="block" style={{ color: "var(--ek-primary)" }}>what actually happened.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.16 }}
            className="text-base mt-6 max-w-xl mx-auto"
            style={{ color: "var(--ek-text-secondary)" }}
          >
            EvidenceKeeper turns your story into observable facts, preserves your evidence with
            military-grade encryption, and produces court-ready reports — without the noise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.24 }}
            className="mt-10 flex flex-col items-center gap-6"
          >
            <button
              onClick={onEnter}
              data-testid="enter-vault-btn"
              className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-md font-head font-semibold tracking-tight text-white transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--ek-primary)", boxShadow: "0 12px 40px -12px rgba(46,130,246,0.6)" }}
            >
              Enter the Vault
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-2">
              {FEATURES.map(({ Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2 text-xs" style={{ color: "var(--ek-text-muted)" }}>
                  <Icon className="h-3.5 w-3.5" style={{ color: "var(--ek-secure)" }} /> {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 max-w-6xl mx-auto w-full px-6 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center">
        <a href="/privacy" className="ek-mono-label hover:text-slate-200" data-testid="splash-privacy-link">PRIVACY</a>
        <a href="/terms" className="ek-mono-label hover:text-slate-200" data-testid="splash-terms-link">TERMS</a>
        <span className="ek-mono-label">PROUDLY CREATED BY HERRELL HOMESTEAD LLC</span>
      </footer>
    </div>
  );
}
